/**
 * WFA API Service
 * 
 * Handles API calls for Walk Forward Analysis operations.
 * Completely separate from optimization API service.
 */

import { apiClient } from '../api/client';

export interface WFAConfig {
  strategy: string;
  symbol: string;
  interval: string;
  analysisStartDate: string;
  analysisEndDate: string;
  inSamplePercentage: number;
  periodLength: number;
  anchoredMode: boolean;
  parameterRanges?: Record<string, { min: number; max: number; step: number }>;
  portfolioAmount?: number;
  portfolioCurrency?: string;
  optimizationTarget?: string;
  windowSizeRange?: {
    enabled: boolean;
    min: number;
    max: number;
    step: number;
    controlMode: 'total' | 'oos';
    cyclesPerWindow?: number;
  };
  /** Parameter policy: 'optimize' runs optimization per window, 'fixed' validates provided params */
  paramPolicy?: {
    mode: 'optimize' | 'fixed';
    params?: Record<string, number>;  // Required when mode is 'fixed'
    bounds?: Record<string, { min: number; max: number; step: number }>;  // Optional for 'optimize'
  };
  periodsAreCandles?: boolean;
  inSampleCandles?: number;
  outSampleCandles?: number;
  stepCandles?: number;
  startCandle?: number;  // Candle range support
  endCandle?: number;    // Candle range support
  /** Optimization version this WFA is linked to (WFA is always a child of an optimization) */
  optimizationVersionId?: string;
  /** Project ID for linking saved WFA results to the project */
  projectId?: string;
}

export interface WFAPeriodMetrics {
  totalReturn: number;
  sharpeRatio: number;
  maxDrawdown?: number;
  maxDrawdownPercent?: number;
  totalTrades: number;
  winRate: number;
  profitFactor: number;
}

export interface WFAEquityCurvePoint {
  timestamp: string;
  portfolioValue: number;
  tradeIndex: number;
}

export interface WFAPeriodResult {
  periodIndex: number;
  parameters: Record<string, number>;
  // Legacy flat structure
  inSampleMetrics?: WFAPeriodMetrics;
  outSampleMetrics?: WFAPeriodMetrics;
  // New nested structure from WFAService
  inSampleResult?: {
    parameters: Record<string, number>;
    metrics: WFAPeriodMetrics;
    score: number;
    tradeCount: number;
  };
  outSampleResult?: {
    parameters: Record<string, number>;
    metrics: WFAPeriodMetrics;
    score: number;
    trades?: Array<Record<string, unknown>>;
    tradeCount: number;
    equityCurve?: WFAEquityCurvePoint[];
  };
  /** Optimization metadata - indicates whether optimization was skipped (fixed params mode) */
  optimizationMeta?: {
    skipped: boolean;
    reason?: 'fixed_params' | 'no_best_result' | 'optimization_error';
    mode: 'optimize' | 'fixed';
    score?: number;
    testedCombinations?: number;
    error?: string;
  };
  trainingStart: string;
  trainingEnd: string;
  testingStart: string;
  testingEnd: string;
}

export interface WFASummary {
  totalPeriods: number;
  successfulPeriods: number;
  avgInSampleReturn: number;
  avgOutSampleReturn: number;
  avgInSampleSharpe: number;
  avgOutSampleSharpe: number;
  avgOutSampleMaxDrawdown?: number; // Out-of-sample max drawdown (decimal, e.g., 0.15 = 15%)
  winRate: number; // Out-of-sample win rate (0-1)
  consistency: number;
  robustness: string; // "Excellent", "Good", "Fair", or "Poor"
  returnStandardDeviation?: number;
  overfittingMetrics: {
    wfe: number; // Walk Forward Efficiency
    performanceDrop: number;
    sharpeRatioDrop: number;
    winRateDrop: number;
    inSampleAnnualized?: number; // Annualized in-sample return (decimal, e.g., 0.78 = 78%)
    outSampleAnnualized?: number; // Annualized out-of-sample return (decimal)
  };
  bestPeriod?: WFAPeriodResult;
  worstPeriod?: WFAPeriodResult;
}

export interface WFAJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  config: WFAConfig;
  progress: number;
  currentPeriod: number;
  totalPeriods: number;
  currentPhase: string;
  startTime: number;
  completedAt?: number;
  summary?: WFASummary;
  periodResults?: WFAPeriodResult[];
  error?: string;
  elapsedTime?: number;
  estimatedTimeRemaining?: number;
}

export interface WFAStartResponse {
  success: boolean;
  jobId?: string;
  message?: string;
  error?: string;
  validationErrors?: string[];
  suggestions?: string[];
}

export interface WindowSizePreviewResponse {
  success: boolean;
  data?: {
    windowSize: number;
    estimatedTrades: number;
    inSampleCandles: number;
    outSampleCandles: number;
    totalCandles: number;
    executionTime: number;
    averageTradesPerDay?: number;
    error?: string;
    message?: string;
  };
  error?: string;
  details?: string;
}

export interface WFAJobsResponse {
  success: boolean;
  jobs?: WFAJob[];
  error?: string;
}

export interface WFAJobResponse {
  success: boolean;
  job?: WFAJob;
  error?: string;
}

export interface SavedWFAQuickStats {
  totalPeriods?: number;
  totalReturn?: number;
  sharpeRatio?: number;
  maxDrawdown?: number;
  winRate?: number;
  profitFactor?: number;
  wfe?: number;
  consistency?: number;
  // Window size range specific
  windowSizesTested?: number;
  bestWindowSize?: number;
  avgTimePerWindow?: number;
}

export interface WFAWindowSizeRange {
  enabled: boolean;
  min: number;
  max: number;
  step: number;
  controlMode: 'total' | 'oos';
}

export interface WFAResult {
  summary: WFASummary;
  periodResults?: WFAPeriodResult[];
  // For window size range analysis
  analyses?: Array<{
    windowSize: number;
    metrics: WFAPeriodMetrics;
    result: {
      periodResults: WFAPeriodResult[];
      summary: WFASummary;
    };
  }>;
  bestWindowSize?: {
    bySharpe?: number;
    byReturn?: number;
  };
  type?: 'wfa' | 'window_size_range';
  optimizationTarget?: string;
}

export interface SavedWFA {
  _id: string;
  userId: string;
  name: string;
  description: string;
  symbol: string;
  interval: string;
  strategy: string;
  type: 'wfa' | 'window_size_range';
  optimizationTarget?: 'totalReturn' | 'sharpeRatio' | 'winRate' | 'profitFactor' | 'maxDrawdown' | 'all';
  analysisStartDate: string;
  analysisEndDate: string;
  walkForwardConfig?: WFAConfig;
  windowSizeRange?: WFAWindowSizeRange;
  parameterRanges?: Record<string, { min: number; max: number; step: number }>;
  result?: WFAResult; // Full result only loaded in detail view
  quickStats: SavedWFAQuickStats;
  createdAt: string;
  updatedAt: string;
}

export interface SaveWFARequest {
  name: string;
  description?: string;
  type?: 'wfa' | 'window_size_range';
  config: WFAConfig;
  result: WFAResult;
}

export interface SavedWFAListResponse {
  success: boolean;
  data?: SavedWFA[];
  pagination?: {
    total: number;
    limit: number;
    skip: number;
    hasMore: boolean;
  };
  error?: string;
}

export interface SavedWFAResponse {
  success: boolean;
  data?: SavedWFA;
  error?: string;
}

export interface SaveWFAResponse {
  success: boolean;
  data?: {
    id: string;
    name: string;
    createdAt: string;
  };
  error?: string;
}

class WFAApiService {
  /**
   * Helper to extract error message from unknown error
   */
  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unknown error occurred';
  }

  /**
   * Start a new WFA analysis
   */
  async startWFA(config: WFAConfig): Promise<WFAStartResponse> {
    try {
      const data = await apiClient.post<WFAStartResponse>('/wfa/start', config);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to start WFA analysis'
      };
    }
  }

  /**
   * Validate WFA configuration without starting
   */
  async validateWFA(config: WFAConfig): Promise<WFAStartResponse> {
    try {
      const data = await apiClient.post<WFAStartResponse>('/wfa/validate', config);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to validate WFA configuration'
      };
    }
  }

  /**
   * Get all WFA jobs
   */
  async getWFAJobs(): Promise<WFAJobsResponse> {
    try {
      const data = await apiClient.get<WFAJobsResponse>('/wfa/jobs');
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch WFA jobs'
      };
    }
  }

  /**
   * Get specific WFA job with full details
   */
  async getWFAJob(jobId: string): Promise<WFAJobResponse> {
    try {
      const data = await apiClient.get<WFAJobResponse>(`/wfa/jobs/${jobId}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch WFA job'
      };
    }
  }

  /**
   * Remove a WFA job
   */
  async removeWFAJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const data = await apiClient.delete<{ success: boolean; error?: string; message?: string }>(`/wfa/jobs/${jobId}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to remove WFA job'
      };
    }
  }

  /**
   * Store a completed WFA job in the database for persistence
   * This allows the job to survive page refreshes and appear in history
   */
  async storeCompletedJob(job: WFAJob): Promise<{ success: boolean; error?: string }> {
    try {
      const data = await apiClient.post<{ success: boolean; error?: string; message?: string }>('/wfa/jobs/store', job);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to store WFA job'
      };
    }
  }

  /**
   * Save a WFA result for later viewing
   */
  async saveWFA(request: SaveWFARequest): Promise<SaveWFAResponse> {
    try {
      const data = await apiClient.post<SaveWFAResponse>('/wfa/save', request);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to save WFA result'
      };
    }
  }

  /**
   * Get all saved WFA results
   */
  async getSavedWFAs(params?: { 
    type?: 'wfa' | 'window_size_range';
    strategy?: string;
    limit?: number;
    skip?: number;
  }): Promise<SavedWFAListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.type) queryParams.append('type', params.type);
      if (params?.strategy) queryParams.append('strategy', params.strategy);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.skip) queryParams.append('skip', params.skip.toString());
      
      const url = `/wfa/saved${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const data = await apiClient.get<SavedWFAListResponse>(url);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch saved WFA results'
      };
    }
  }

  /**
   * Get a specific saved WFA result with full details
   */
  async getSavedWFA(id: string): Promise<SavedWFAResponse> {
    try {
      const data = await apiClient.get<SavedWFAResponse>(`/wfa/saved/${id}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch saved WFA result'
      };
    }
  }

  /**
   * Get WFA results linked to a specific optimization version.
   * WFA is always a child of an optimization version.
   */
  async getWFAsByOptimizationVersion(versionId: string): Promise<SavedWFAListResponse> {
    try {
      const data = await apiClient.get<SavedWFAListResponse>(`/wfa/saved/by-optimization/${versionId}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch WFAs for optimization version'
      };
    }
  }

  /**
   * Batch check which optimization versions have linked WFAs.
   * Returns a map of version ID -> WFA ID (or null if no WFA exists).
   */
  async checkVersionsForWFA(versionIds: string[]): Promise<{ 
    success: boolean; 
    data?: Record<string, string | null>;
    error?: string;
  }> {
    try {
      const data = await apiClient.post<{ 
        success: boolean; 
        data: Record<string, string | null>;
        error?: string;
      }>('/wfa/saved/check-versions', { versionIds });
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to check WFA existence for versions'
      };
    }
  }

  /**
   * Batch check which baseline optimization IDs have completed pack WFAs.
   * Returns a map of baseline ID -> pack WFA badge info (or null).
   */
  async checkBaselinesForPackWFA(baselineIds: string[]): Promise<{
    success: boolean;
    data?: Record<string, PackWFABadgeInfo | null>;
    error?: string;
  }> {
    try {
      const data = await apiClient.post<{
        success: boolean;
        data: Record<string, PackWFABadgeInfo | null>;
      }>('/wfa/pack/check-baselines', { baselineIds });
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to check baselines for pack WFA'
      };
    }
  }

  /**
   * Delete a saved WFA result
   */
  async deleteSavedWFA(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const data = await apiClient.delete<{ success: boolean; message?: string; error?: string }>(`/wfa/saved/${id}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to delete saved WFA result'
      };
    }
  }

  /**
   * Update a saved WFA result (name, description)
   */
  async updateSavedWFA(id: string, updates: { name?: string; description?: string }): Promise<SavedWFAResponse> {
    try {
      const data = await apiClient.patch<SavedWFAResponse>(`/wfa/saved/${id}`, updates);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to update saved WFA result'
      };
    }
  }

  /**
   * Preview a window size to estimate trade count
   * Fetches candles and runs preview on a sliced subset matching the window size
   */
  async previewWindowSize(windowSize: number, config: Partial<WFAConfig>): Promise<WindowSizePreviewResponse> {
    try {
      // Calculate the date range for the window size
      const controlMode = config.windowSizeRange?.controlMode || 'total';
      const ratio = config.ratio || '3:1';
      const [trainingPart, testingPart] = ratio.split(':').map(Number);
      
      let outSampleCandles: number;
      let totalPeriodCandles: number;
      
      if (controlMode === 'total') {
        // windowSize is the total period
        totalPeriodCandles = windowSize;
        const inSamplePercentage = (trainingPart / (trainingPart + testingPart)) * 100;
        outSampleCandles = Math.floor(windowSize * (1 - inSamplePercentage / 100));
      } else {
        // controlMode === 'oos': windowSize is the OOS period
        outSampleCandles = windowSize;
        totalPeriodCandles = Math.floor((windowSize * (trainingPart + testingPart)) / testingPart);
      }
      
      // Build parameter object with default values from strategy registry
      const parameters: Record<string, number> = {};
      if (config.parameterRanges) {
        for (const [key, range] of Object.entries(config.parameterRanges)) {
          // Use the 'fixed' value (default) if available, otherwise use mid-range
          parameters[key] = range.fixed !== undefined ? range.fixed : Math.floor((range.min + range.max) / 2);
        }
      }
      
      const startTime = Date.now();
      
      // Step 1: Fetch all candles to get the full dataset
      // Request a large limit to cover all available data
      const candlesResponse = await apiClient.get<any>(`/candles/${config.symbol}/${config.interval}?limit=100000`);
      
      if (!candlesResponse || !candlesResponse.data || candlesResponse.data.length === 0) {
        return {
          success: false,
          error: `No candle data available for ${config.symbol} ${config.interval}`
        };
      }
      
      const allCandles = candlesResponse.data;
      
      // Step 2: Slice the last N candles based on totalPeriodCandles
      // Use the most recent data since that's what WFA will test
      const slicedCandles = allCandles.slice(-totalPeriodCandles);
      
      if (slicedCandles.length < totalPeriodCandles) {
        return {
          success: false,
          error: `Not enough candles: need ${totalPeriodCandles}, have ${allCandles.length}`
        };
      }
      
      // Step 3: Run preview with the sliced candles
      const backtestRequest = {
        strategy: config.strategy || '',
        symbol: config.symbol || '',
        interval: config.interval || '',
        candles: slicedCandles, // Pass the sliced candles
        params: parameters,
        portfolioAmount: config.portfolioAmount || 10000,
        portfolioCurrency: config.portfolioCurrency || 'USDT',
        portfolioType: 'USD'
      };
      
      const response = await apiClient.post<any>('/backtests/preview', backtestRequest);
      
      const executionTime = Date.now() - startTime;
      
      // Response structure: { success: true, data: { trades: [...], stats: {...}, ... } }
      if (response && response.data && response.data.trades) {
        return {
          success: true,
          data: {
            windowSize,
            estimatedTrades: response.data.trades.length,
            inSampleCandles: Math.floor(totalPeriodCandles * (trainingPart / (trainingPart + testingPart))),
            outSampleCandles,
            totalCandles: totalPeriodCandles,
            executionTime,
            availableCandles: allCandles.length // Show user how much data is available
          }
        };
      } else {
        return {
          success: false,
          error: 'Invalid response from preview'
        };
      }
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to preview window size'
      };
    }
  }

  // ============================================================================
  // Pack WFA (Multi-Asset) Methods
  // ============================================================================

  /**
   * Start a pack WFA run across multiple assets
   */
  async startPackWFA(config: PackWFAConfig): Promise<PackWFAStartResponse> {
    try {
      const data = await apiClient.post<PackWFAStartResponse>('/wfa/pack', config);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to start pack WFA'
      };
    }
  }

  /**
   * Get pack WFA status and results
   */
  async getPackWFA(packWfaId: string): Promise<PackWFAResponse> {
    try {
      const data = await apiClient.get<PackWFAResponse>(`/wfa/pack/${packWfaId}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        error: this.getErrorMessage(error) || 'Failed to fetch pack WFA'
      };
    }
  }

  /**
   * Get pack WFA details for analysis page (summary or full mode)
   */
  async getPackWfaDetails(packWfaId: string, mode: 'summary' | 'full' = 'summary'): Promise<PackWfaDetailsResponse> {
    try {
      const data = await apiClient.get<PackWfaDetailsResponse>(`/wfa/pack/${packWfaId}/details?mode=${mode}`);
      return data;
    } catch (error: unknown) {
      return {
        success: false,
        resultVersion: 2,
        data: null,
        error: this.getErrorMessage(error) || 'Failed to fetch pack WFA details',
      };
    }
  }
}

// ============================================================================
// Pack WFA Types
// ============================================================================

export interface PackWFABadgeInfo {
  packWfaId: string;
  assetsPassed: number;
  assetsTotal: number;
  verdict: string | null;
}

export interface PackWFAConfig {
  baselineOptimizationId: string;
  assets: string[];
  sharedParams: Record<string, number>;
  wfaConfig: {
    strategy: string;
    interval: string;
    inSamplePercentage: number;
    periodLength: number;
    stepCandles?: number;
    periodsAreCandles?: boolean;
    optimizationTarget?: string;
    portfolioAmount?: number;
    portfolioCurrency?: string;
    parameterRanges?: Record<string, { min: number; max: number; step: number }>;
    startCandle?: number;
    endCandle?: number;
  };
}

export interface PackWFAAssetResult {
  asset: string;
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'skipped';
  jobId?: string;
  savedWfaId?: string;
  quickStats?: {
    oosReturn?: number;
    oosSharpe?: number;
    maxDrawdown?: number;
    periodsPassed?: number;
    periodsTotal?: number;
    passRate?: number;
  };
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface PackWFASummary {
  totalAssets: number;
  completedAssets: number;
  failedAssets: number;
  skippedAssets: number;
  failedAssetList?: string[];
  medianOosReturn: number;
  worstOosReturn: number;
  medianMaxDrawdown: number;
  worstMaxDrawdown: number;
  medianPassRate: number;
  medianOosSharpe: number;
  verdict: 'strong' | 'moderate' | 'weak' | 'fail';
}

export interface PackWFA {
  _id: string;
  userId: string;
  baselineOptimizationId: string;
  projectId?: string;
  sharedParamsSnapshot: Record<string, number>;
  wfaConfigSnapshot: Record<string, unknown>;
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled';
  progress: {
    currentAsset: string;
    currentAssetIndex: number;
    totalAssets: number;
    completedAssets: number;
    failedAssets: number;
  };
  assetResults: PackWFAAssetResult[];
  summary?: PackWFASummary;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  error?: string;
}

export interface PackWFAStartResponse {
  success: boolean;
  data?: {
    packWfaId: string;
    status: string;
    assetCount: number;
    queuedJobs: Array<{ asset: string; jobId: string; status: string }>;
  };
  error?: string;
}

export interface PackWFAResponse {
  success: boolean;
  data?: PackWFA;
  error?: string;
}

export interface PackWfaDetailsAsset {
  asset: string;
  savedWfaId: string | null;
  status: 'ok' | 'missing_result' | 'failed' | 'skipped';
  windowCount: number;
  error?: string;
  summary: WFASummary | null;
  periodResults: WFAPeriodResult[];
  periodSummaries: Array<{
    periodIndex: number;
    period: { start: string; end: string; isStart: string; isEnd: string; oosStart: string; oosEnd: string } | null;
    success: boolean;
    isMetrics: WFAPeriodMetrics | null;
    oosMetrics: WFAPeriodMetrics | null;
    oosParams: Record<string, number> | null;
  }>;
  aggregatedMetrics: {
    is: { avgReturn: number; avgSharpe: number; avgMaxDD: number };
    oos: { avgReturn: number; avgSharpe: number; avgMaxDD: number };
  } | null;
}

export interface PackWfaDetailsResponse {
  success: boolean;
  resultVersion: number;
  data: {
    packWfa: PackWFA;
    assetDetails: PackWfaDetailsAsset[];
  } | null;
  error?: string;
}

export const wfaApiService = new WFAApiService();
export default wfaApiService;
